"use client";
import { DayPart } from "@/dto/dayPartMenu";
import { Merchant } from "@/dto/merchant";
import { OrderType } from "@/types";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import { getDayPartMenuAvailableTime } from "@/utils/dayPartMenuUtils";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { CSSProperties } from "react";
import Skeleton from "react-loading-skeleton";

export interface DayPartMenuSelectorProps {
  dayPartMenus: DayPart[];
  selectedMenu: DayPart | null;
  merchant: Merchant;
  style?: CSSProperties;
  showBottomBorder?: boolean;
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (menu: DayPart | null) => void;
}

export const DayPartMenuSelector = ({
  dayPartMenus,
  selectedMenu,
  merchant,
  style,
  showBottomBorder,
  onMenuChange,
}: DayPartMenuSelectorProps) => {
  const searchParams = useSearchParams();
  const isDineIn =
    searchParams?.get("order_type")?.toLowerCase() ===
    OrderType.DINE_IN.toLowerCase();

  const onMenuSelect = (menu: DayPart, close: () => void) => {
    close();
    onMenuChange(menu);
  };

  const onMenuDropdownClick = () => {
    if (!isDineIn) return;

    onMenuChange(selectedMenu);
  };

  return (
    <Popover
      style={style}
      className={`relative ${showBottomBorder ? "border-b-4 pb-2" : "pb-3"}`}
    >
      {({ close }) => (
        <>
          <PopoverButton
            className="flex justify-between items-center h-full px-3 gap-x-1 text-sm w-screen max-w-[400px] leading-6 text-gray-900 outline-none focus:outline-none"
            onClick={onMenuDropdownClick}
          >
            {selectedMenu ? (
              <>
                <p className="flex justify-start h-full text-left gap-x-2">
                  <span className="flex items-center font-bold text-base leading-tight">
                    {selectedMenu.menuName.length > 35
                      ? selectedMenu.menuName.slice(0, 35) + "..."
                      : selectedMenu.menuName}
                  </span>
                  <span className="flex items-center text-[#5d5d5d] text-sm font-normal shrink-0">
                    {getDayPartMenuAvailableTime(
                      selectedMenu,
                      merchant.timezone
                    )}
                  </span>
                </p>
                {!isDineIn && (
                  <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                )}
              </>
            ) : (
              <div className="flex flex-col sm:flex-row w-full justify-between gap-4 sm:items-center items-start">
                <Skeleton
                  className="h-full"
                  containerClassName="flex items-center justify-center font-semibold text-[30px] leading-[45px] h-[30px] w-full"
                  inline={true}
                />
              </div>
            )}
          </PopoverButton>

          {!isDineIn && (
            <PopoverPanel
              transition
              className="absolute left-0 z-10 mt-3 flex w-screen max-w-[400px] transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[enter]:ease-out data-[leave]:duration-150 data-[leave]:ease-in z-[20]"
            >
              <div className="w-full shrink rounded-b-xl bg-white text-sm font-semibold leading-6 text-gray-900 shadow-lg ring-1 ring-gray-900/5">
                <DayPartMenuList
                  dayPartMenus={dayPartMenus}
                  selectedMenu={selectedMenu}
                  merchant={merchant}
                  onMenuSelect={(m) => onMenuSelect(m, close)}
                />
              </div>
            </PopoverPanel>
          )}
        </>
      )}
    </Popover>
  );
};

interface DayPartMenuListProps {
  dayPartMenus: DayPart[];
  selectedMenu: DayPart | null;
  merchant: Merchant;
  // eslint-disable-next-line no-unused-vars
  onMenuSelect: (menu: DayPart) => void;
}

export function DayPartMenuList({
  dayPartMenus,
  selectedMenu,
  merchant,
  onMenuSelect,
}: DayPartMenuListProps) {
  const t = useTranslations("DayPartMenu");
  return (
    <>
      {dayPartMenus.map((m, index) => (
        <div
          className={`flex justify-between items-center gap-x-2 block py-2 px-4 hover:bg-gray-100 cursor-pointer ${
            m.id === selectedMenu?.id ? "bg-gray-100" : ""
          } ${index === dayPartMenus.length - 1 ? "rounded-b-xl" : ""}`}
          key={m.id}
          onClick={() => onMenuSelect(m)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onMenuSelect(m);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div className="flex justify-start items-center gap-x-2 overflow-hidden">
            <div className="flex flex-col justify-start gap-y-1 w-full">
              <div className="flex justify-start items-start">
                <span className="font-semibold text-base text-ellipsis overflow-hidden">
                  {m.menuName}
                </span>
                {m.activeNow && (
                  <>
                    <span className="text-[#280] font-semibold text-xl pl-1">
                      &#xb7;
                    </span>
                    &nbsp;
                    <span className="text-[#280] font-semibold">
                      {t("now")}
                    </span>
                  </>
                )}
              </div>
              <span className="text-[#5d5d5d] text-sm font-normal">
                {m.availabilityLabel}
              </span>
            </div>
          </div>
          <span className="text-[#5d5d5d] text-sm font-normal shrink-0">
            {getDayPartMenuAvailableTime(m, merchant.timezone)}
          </span>
        </div>
      ))}
    </>
  );
}

interface MobileDayPartMenuListProps extends DayPartMenuListProps {
  style?: CSSProperties;
  showBottomBorder?: boolean;
}
export function MobileDayPartMenuList({
  dayPartMenus,
  selectedMenu,
  merchant,
  onMenuSelect,
}: MobileDayPartMenuListProps) {
  const t = useTranslations("DayPartMenu");
  const merchantColor =
    merchant?.merchantMeta?.colorPalette?.color ||
    cloverColorTheme.Main.primaryColor;
  return (
    <>
      {dayPartMenus.map((m) => (
        <div
          className={`flex justify-between flex-col block hover:bg-gray-100 cursor-pointer gap-y-1 ${
            m.id === selectedMenu?.id ? "border-b-4" : ""
          }`}
          style={{ borderColor: merchantColor }}
          key={m.id}
          onClick={() => onMenuSelect(m)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onMenuSelect(m);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div className="flex justify-between items-center">
            <span
              className={`${
                m.id === selectedMenu?.id ? "font-bold" : ""
              } text-sm text-[#2E2E2E]`}
            >
              {m.menuName}
            </span>
            {m.activeNow && (
              <div className="flex items-center">
                &nbsp;
                <span className="text-[#280] font-semibold text-xl pt-0.5">
                  &#xb7;
                </span>
                &nbsp;
                <span className="text-[#280] font-semibold">{t("now")}</span>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center mb-2">
            <span className="text-[#5d5d5d] text-xs font-normal">
              {m.availabilityLabel}
            </span>

            <span className="text-[#5d5d5d] text-xs font-normal">
              {getDayPartMenuAvailableTime(m, merchant.timezone)}
            </span>
          </div>
        </div>
      ))}
    </>
  );
}
